/**
 * English labels the genetics screens look up by key at render time. They are catalog keys: the
 * contract lists them in `messages` so the localization check knows to expect Spanish for each.
 */

export const CHECK_LABELS: Record<string, string> = {
  tag: "Calf has a tag",
  birth_date: "Birth date",
  sex: "Sex recorded",
  sire_registered: "Sire registered",
  dam_registered: "Dam registered",
  birth_weight: "Birth weight",
  calving_ease: "Calving ease",
  dna: "DNA parent check",
  et_paperwork: "ET paperwork",
  not_registered: "Not already registered",
};

export const ISSUE_LABELS: Record<string, string> = {
  excluded_linked: "Recorded parent was excluded",
  confirmed_differs: "Confirmed parent differs from the record",
  suspected: "Sire not confirmed",
  failed_sample: "DNA sample failed",
  missing_parent: "Parent missing",
};

export const RELATION_LABELS: Record<string, string> = {
  same: "Same animal",
  parent: "He is her sire",
  offspring: "She is his dam",
  ancestor: "He is her grandsire or older ancestor",
  descendant: "She is his ancestor",
  shared_ancestor: "Shared ancestor",
};

export const RISK_LABELS: Record<string, string> = {
  none_found: "No shared ancestors found",
  low: "Distant relatives",
  watch: "Close relatives",
  high: "Too close",
};

export const DNA_STATUS_LABELS: Record<string, string> = {
  collected: "Sample pulled", sent: "At the lab", resulted: "Results back", failed: "Sample failed",
};

export const CG_FACTOR_LABELS: Record<string, string> = {
  management: "Management group", sex: "Sex", window: "Born", et: "ET calves", treated: "Treated", creep: "Creep fed", twin: "Twins",
};

export const SEX_CLASS_LABELS: Record<string, string> = { heifer: "Heifers", bull: "Bulls", steer: "Steers", unknown: "Sex unknown" };

export const JOB_STAGE_LABELS: Record<string, string> = {
  prepared: "Preparing", exported: "File made", submitted: "Sent", accepted: "Accepted", partial: "Partly accepted", rejected: "Rejected",
  collecting: "Pulling samples", sent: "At the lab", results: "Results back", planning: "Planning", ready: "Ready to breed", breeding: "Breeding",
};

export const RIGHTS_LABELS: Record<string, string> = { full: "Ours to use or sell", use_only: "Use only, no resale", restricted: "Restricted" };
export const SEXED_LABELS: Record<string, string> = { conventional: "Conventional", sexed_female: "Sexed heifer", sexed_male: "Sexed bull", unknown: "Not known" };
export const FREEZE_LABELS: Record<string, string> = { direct_transfer: "Direct transfer", glycerol: "Glycerol", vitrified: "Vitrified", fresh: "Fresh" };

export const MOVE_REASON_LABELS: Record<string, string> = { receive: "Received", use: "Used", adjust: "Adjusted", transfer: "Moved", sell: "Sold", waste: "Thrown out", count: "Counted" };

export const MATING_PRESET_LABELS = { heifers: "Calving ease for heifers", growth: "Growth for feeder calves", maternal: "Keep replacements" } as const;

export const WORKSPACE_SECTION_LABELS: Record<string, string> = {
  overview: "Overview", pedigree: "Pedigrees", epds: "EPDs", dna: "DNA", mating: "Mating plans", groups: "Contemporary groups", traits: "Traits",
  batches: "Batches", transfers: "Transfers",
};

/** Messages the server sends back (validation and errors); the web translates them with t(). */
export const SERVER_MESSAGES: string[] = [
  "A calf can only be in one group per stage.",
  "A category needs its codes.",
  "A dam has to be a female.",
  "A donor has to be a female.",
  "A moved calf must be in the group.",
  "A registered animal needs its registration number.",
  "A sire has to be a male.",
  "A trait with that key already exists. Publish a new version of it instead.",
  "Add at least one EPD or index.",
  "An animal can't be its own parent.",
  "Breeds add up to more than 100%.",
  "Each trait appears once per run.",
  "Frozen and fresh can't add up to more than usable.",
  "List each breed once.",
  "Make the file before marking the batch sent.",
  "More embryos in the tank than were frozen.",
  "No calves in this batch are ready for the file yet.",
  "No pulled samples are waiting in this order.",
  "Not ready",
  "Pick a different lot to move into.",
  "Pick a semen or embryo lot.",
  "Pick the semen lot the straws came from.",
  "Say how many moved.",
  "Say how many you counted.",
  "Say how many.",
  "Say where they went.",
  "Say which lab the samples went to.",
  "Semen comes from a bull.",
  "Some of those calves aren't ready yet.",
  "That calf isn't in this batch.",
  "That female isn't in this plan.",
  "That file has no rows to read.",
  "That isn't an EPD file.",
  "That isn't a DNA results file.",
  "That registration number is already on another animal.",
  "That would make the animal its own ancestor.",
  "The parent was born after this animal.",
  "The sire can't be the donor.",
  "The top of the scale must be above the bottom.",
  "Usable can't be more than recovered.",
  "We couldn't find a barcode, tag or registration column in that file.",
  "We couldn't find a calf ID or tag column in that file.",
  "We couldn't find a tag or registration column in that file.",
  "We couldn't find that animal.",
  "We couldn't find that donor.",
  "We couldn't find that sire.",
  "Use letters, numbers and underscores.",
  "Say why the calf was moved.",
];

export const LABEL_MESSAGES: string[] = [
  ...SERVER_MESSAGES,
  ...Object.values(MATING_PRESET_LABELS), ...Object.values(WORKSPACE_SECTION_LABELS), ...Object.values(MOVE_REASON_LABELS),
  ...Object.values(CHECK_LABELS), ...Object.values(ISSUE_LABELS), ...Object.values(RELATION_LABELS), ...Object.values(RISK_LABELS), ...Object.values(DNA_STATUS_LABELS),
  ...Object.values(CG_FACTOR_LABELS), ...Object.values(SEX_CLASS_LABELS), ...Object.values(JOB_STAGE_LABELS), ...Object.values(RIGHTS_LABELS), ...Object.values(SEXED_LABELS),
  ...Object.values(FREEZE_LABELS),
];
